import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { DialogLayer } from './Dialog';
import { trapTab } from './focus';

import { anyChanged, isPassing, leaveScreen, leaveWithoutAsking, promptFor, setAsk, type UnsavedGuard } from './unsavedCore';

export { leaveApp, leaveGuarded, leaveScreen, leaveWithoutAsking, sameValues, useLeavePrompt, useOpening, useRowGuard, useUnsavedGuard, validForm, type LeavePrompt, type UnsavedGuard } from './unsavedCore';

/** A click on a link to another screen of the app (same tab): the screen it leaves may have a changed form. */
function linkTarget(e: MouseEvent): URL | null {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null;
  const a = e.target instanceof Element ? e.target.closest<HTMLAnchorElement>('a[href]') : null;
  if (!a || (a.target && a.target !== '_self') || a.hasAttribute('download')) return null;
  const url = new URL(a.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  return url;
}

/** The router's place in the browser's history (its `idx`), null when unknown. */
function historyIndex(): number | null {
  const idx = (window.history.state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : null;
}

/** The one dialog of the app (in `AppLayout`), over everything, centred. */
export function UnsavedChangesHost() {
  const [pending, setPending] = useState<{ go: () => void; inside: UnsavedGuard[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const here = useRef('');
  here.current = location.pathname + location.search;

  useLayoutEffect(() => {
    setAsk((go, inside) => setPending({ go, inside }));
    return () => setAsk(null);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (isPassing()) return;
      const url = linkTarget(e);
      // the same screen (only another #part of it, review M4) leaves nothing
      if (url === null || url.pathname + url.search === here.current) return;
      // a changed form, or the screen's own question (owner 2026-10-27 12:41)
      if (!anyChanged() && !promptFor(url.pathname + url.search)) return;
      // before the router's own handler (it listens on the app's root, below the document)
      e.preventDefault();
      e.stopPropagation();
      leaveScreen(url.pathname + url.search, () => navigate(url.pathname + url.search + url.hash));
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [navigate]);

  // The browser's "Wstecz" / "Dalej" (owner 2026-10-23 21:14). The address has already changed when it tells us, and
  // it cannot be stopped: the router is kept from acting on it (this listener goes first: added before the router's,
  // in the capture), the address is put back, and the move is made again after the choice. The router's place in the
  // history (`idx` in its state) says how far it went.
  const at = useRef<number | null>(null);
  useLayoutEffect(() => {
    at.current = historyIndex();
  }, [location]);
  useEffect(() => {
    /** Our own moves: the one putting the address back (kept from the router) and the one made after the choice. */
    let restoring = 0;
    let replaying = 0;
    const onPop = (e: PopStateEvent) => {
      if (replaying > 0) {
        replaying--;
        return;
      }
      if (restoring > 0) {
        restoring--;
        e.stopImmediatePropagation();
        return;
      }
      const to = historyIndex();
      const from = at.current;
      const target = window.location.pathname + window.location.search;
      if (isPassing() || (!anyChanged() && !promptFor(target)) || to === null || from === null || to === from) return;
      if (target === here.current) return;
      e.stopImmediatePropagation();
      const delta = to - from;
      restoring++;
      window.history.go(-delta);
      leaveScreen(target, () => {
        replaying++;
        window.history.go(delta);
      });
    };
    window.addEventListener('popstate', onPop, true);
    return () => window.removeEventListener('popstate', onPop, true);
  }, []);

  if (!pending) return null;
  const cancel = () => setPending(null);
  const go = () => {
    setPending(null);
    leaveWithoutAsking(pending.go);
  };
  const discard = () => {
    for (const g of pending.inside) g.discard?.();
    go();
  };
  const save = async () => {
    setBusy(true);
    let saved = true;
    for (const g of pending.inside) {
      try {
        saved = await g.save(null);
      } catch {
        saved = false;
      }
      if (!saved) break;
    }
    setBusy(false);
    // not saved: the form stays open and shows why
    if (saved) go();
    else cancel();
  };

  return createPortal(
    <div className="dialog-backdrop">
      <div
        className="dialog panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="unsaved-title"
        aria-describedby="unsaved-text"
        onKeyDown={(e) => {
          if (e.key !== 'Tab') return;
          trapTab(e.currentTarget, e.nativeEvent);
          // a dialog under it (a task) traps Tab on the document: the focus stays here
          e.stopPropagation();
        }}
      >
        <DialogLayer onClose={cancel} busy={busy} />
        <h2 id="unsaved-title">Masz niezapisane zmiany</h2>
        <p id="unsaved-text">Zapisać je przed zamknięciem?</p>
        <div className="actions">
          <button className="btn primary" type="button" autoFocus disabled={busy} onClick={() => void save()}>
            {busy ? 'Zapisywanie…' : 'Zapisz'}
          </button>
          <button className="btn" type="button" disabled={busy} onClick={discard}>
            Nie zapisuj
          </button>
          <button className="btn ghost" type="button" disabled={busy} onClick={cancel}>
            Anuluj
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
